import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

/**
 * Urlopy podwykonawcy — dni, w których CAŁA firma nie pracuje. Zgłasza je konto firmy
 * w portalu („Moje urlopy"; konto z „Widocznością stawek", ta sama flaga co kwoty i
 * faktury) albo osoba z uprawnieniem do podwykonawców na karcie firmy. Firma z urlopem w oknie składania zamówienia (dziś + 7 dni na potwierdzenie)
 * ma `on_leave` na listach firm: pickery i podpowiedź spychają ją na dół listy z dopiskiem
 * „(urlop od–do)". Nadal można ją wybrać.
 */

export type LeavePhase = "upcoming" | "ongoing" | "past" | "cancelled";

export interface ContractorLeave {
  id: string;
  contractor: string;
  start_date: string;
  end_date: string;
  note: string;
  status: "active" | "cancelled";
  phase: LeavePhase;
  days: number;
  /** Pierwotny koniec sprzed skrócenia (null = nie skracany). */
  original_end_date: string | null;
  can_cancel: boolean;
  can_shorten: boolean;
  created_by: string | null;
  created_by_name: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancelled_by_name: string;
  created_at: string;
  updated_at: string;
}

/** Najbliższy urlop firmy w oknie składania zamówienia (pole `on_leave` firmy). */
export interface OnLeaveBrief {
  id: string;
  start_date: string;
  end_date: string;
  is_ongoing: boolean;
}

export interface ContractorLeavesResult {
  leaves: ContractorLeave[];
  /** Data „dziś" po stronie serwera — reguły skracania liczą się względem niej. */
  today: string;
}

export const ORDER_CONFIRMATION_WINDOW_DAYS = 7;

export const PHASE_LABELS: Record<LeavePhase, string> = {
  upcoming: "Zaplanowany",
  ongoing: "Trwa",
  past: "Miniony",
  cancelled: "Wycofany",
};

export function leavesQueryKey(contractorId: string) {
  return ["contractor-leaves", contractorId] as const;
}

export function useContractorLeaves(contractorId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: leavesQueryKey(contractorId ?? ""),
    queryFn: async (): Promise<ContractorLeavesResult> => {
      const r = await apiClient.get<{ data: ContractorLeave[]; meta: { today?: string } }>(
        `/contractors/${contractorId}/leaves/`,
      );
      return { leaves: r.data.data, today: r.data.meta?.today ?? todayISO() };
    },
    enabled: enabled && !!contractorId,
  });
}

function useInvalidateLeaves(contractorId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: leavesQueryKey(contractorId) });
    // Listy i karta firmy niosą `on_leave`; podpowiedź firmy liczy urlop na nowo.
    qc.invalidateQueries({ queryKey: ["contractors"] });
  };
}

export interface CreateLeavePayload {
  start_date: string;
  end_date: string;
  note?: string;
}

export function useCreateContractorLeave(contractorId: string) {
  const invalidate = useInvalidateLeaves(contractorId);
  return useMutation({
    mutationFn: (payload: CreateLeavePayload) =>
      apiClient
        .post<{ data: ContractorLeave }>(`/contractors/${contractorId}/leaves/`, payload)
        .then((r) => r.data.data),
    onSuccess: invalidate,
  });
}

export interface ShortenLeavePayload {
  id: string;
  start_date?: string;
  end_date?: string;
}

export function useShortenContractorLeave(contractorId: string) {
  const invalidate = useInvalidateLeaves(contractorId);
  return useMutation({
    mutationFn: ({ id, ...payload }: ShortenLeavePayload) =>
      apiClient
        .patch<{ data: ContractorLeave }>(`/contractors/${contractorId}/leaves/${id}/`, payload)
        .then((r) => r.data.data),
    onSuccess: invalidate,
  });
}

export function useCancelContractorLeave(contractorId: string) {
  const invalidate = useInvalidateLeaves(contractorId);
  return useMutation({
    mutationFn: (id: string) =>
      apiClient
        .post<{ data: ContractorLeave }>(`/contractors/${contractorId}/leaves/${id}/cancel/`)
        .then((r) => r.data.data),
    onSuccess: invalidate,
  });
}

// ── daty (yyyy-mm-dd, bez stref) ──────────────────────────────────────────────

export function todayISO(): string {
  const d = new Date();
  return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  return toISO(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function maxISO(a: string, b: string): string {
  return a >= b ? a : b;
}

/** `2026-10-12` → `12.10.2026` (bez przeliczania stref). */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

export function formatLeaveSpan(l: { start_date: string; end_date: string }): string {
  if (l.start_date === l.end_date) return formatDay(l.start_date);
  return `${formatDay(l.start_date)} – ${formatDay(l.end_date)}`;
}

export function daysLabel(n: number): string {
  if (n === 1) return "1 dzień";
  return `${n} dni`;
}

/** Krótka etykieta do plakietki na liście / karcie firmy: „Urlop 12.10.2026–20.10.2026". */
export function onLeaveLabel(brief: OnLeaveBrief): string {
  return `Urlop ${formatDay(brief.start_date)}–${formatDay(brief.end_date)}`;
}

/** Dopisek do opcji pickera: „Nazwa (urlop 12.10.2026–20.10.2026)". */
export function withLeaveSuffix(label: string, brief: OnLeaveBrief | null | undefined): string {
  return brief ? `${label} (urlop ${formatDay(brief.start_date)}–${formatDay(brief.end_date)})` : label;
}

/** Opcje pickera: alfabetycznie, firmy na urlopie na dole (stabilnie, też alfabetycznie). */
export function sortLeaveLast<T extends { label: string; onLeave?: boolean }>(options: T[]): T[] {
  return [...options].sort((a, b) => {
    if (!!a.onLeave !== !!b.onLeave) return a.onLeave ? 1 : -1;
    return a.label.localeCompare(b.label);
  });
}

/** Spłaszcza `errors` z koperty API do mapy pole → pierwszy komunikat. */
export function flattenApiErrors(err: unknown): Record<string, string> {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  const out: Record<string, string> = {};
  if (!errors || typeof errors !== "object") return out;
  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(value)) out[key] = String(value[0] ?? "");
    else if (value && typeof value === "object") out[key] = JSON.stringify(value);
    else out[key] = String(value);
  }
  return out;
}

export function firstApiError(err: unknown, fallback: string): string {
  const flat = flattenApiErrors(err);
  return flat.detail || Object.values(flat)[0] || fallback;
}
